<div class="">
  <div class="sticky top-0 z-10 -mt-6 space-y-6 bg-white py-6">
    @if (showDebug) {
      <div
        data-test-id="debugHeader"
        class="-mt-6 flex flex-wrap gap-6 rounded-xl bg-grey-200 px-2 py-3 text-xs"
      >
        <p>address.isResoluted: {{ address?.isResoluted | json }}</p>
        <p>showPreviewOnly: {{ showPreviewOnly | json }}</p>
        <p>
          unresolvedAttributeConflicts:
          {{ unresolvedAttributeConflicts | json }}
        </p>
        <p>
          addressConflictCounts:
          {{ addressConflictCounts | json }}
        </p>
        <p>
          attributeConflictCounts:
          {{ attributeConflictCounts | json }}
        </p>
      </div>
    }

    <div
      class="flex flex-wrap items-start justify-end gap-6 bg-white md:flex-nowrap"
    >
      <div
        class="flex shrink grow basis-[min-content] flex-wrap items-start gap-x-6"
      >
        <app-page-headline
          [headline]="'pages.conflictDetail.headline' | translate"
        />
        <div class="flex gap-4">
          @if (addressConflictCounts['NEW_ADDRESS']) {
            <app-number
              data-test-id="conflictInfo-NEW_ADDRESS"
              class="whitespace-nowrap"
              type="notice"
              size="default"
            >
              {{ 'models.conflict.isNewAddress.label' | translate }}
            </app-number>
          }
          @if (allConflictCounts['MASTER_DATA_CONFLICT']) {
            <app-number
              data-test-id="conflictInfo-MASTER_DATA_CONFLICT"
              class="whitespace-nowrap"
              type="error"
              size="default"
            >
              {{
                `${('models.conflict.isConflictAddress.label' | translate)}: ${allConflictCounts['MASTER_DATA_CONFLICT']}`
              }}
            </app-number>
          }
          @if (allConflictCounts['DUPLICATE_IN_CSV']) {
            <app-number
              data-test-id="conflictInfo-DUPLICATE_IN_CSV"
              class="whitespace-nowrap"
              type="warning"
              size="default"
            >
              {{
                `${('models.conflict.isDuplicateInCsv.label' | translate)}: ${allConflictCounts['DUPLICATE_IN_CSV']}`
              }}
            </app-number>
          }
          @if (allConflictCounts['MOVEMENT_DATA_CONFLICT']) {
            <app-number
              data-test-id="conflictInfo-MOVEMENT_DATA_CONFLICT"
              class="whitespace-nowrap"
              type="warning"
              size="default"
            >
              {{
                `${('models.conflict.isDuplicateWithChange.label' | translate)}: ${allConflictCounts['MOVEMENT_DATA_CONFLICT']}`
              }}
            </app-number>
          }
        </div>
      </div>
      <div class="flex flex-wrap justify-end gap-2">
        <app-back-button
          [text]="
            viewMode === 'clearing'
              ? ('pages.conflictDetail.cancelAndBackButton.label' | translate)
              : ('pages.conflictDetail.backButton.label' | translate)
          "
        />

        @if (viewMode === 'clearing') {
          <app-button
            data-test-id="saveAndBackButton"
            nz-tooltip
            nzTooltipColor="#DF1A1A"
            type="primary"
            [disabled]="openAttributeConflicts !== 0 || isLoading"
            [nzTooltipTitle]="
              openAttributeConflicts !== 0
                ? ('pages.conflictDetail.saveButton.toolTip' | translate)
                : undefined
            "
            (trigger)="onSaveAndNext()"
          >
            <svg-icon src="assets/icons/General/save-01.svg" />
            @if (hasNextConflict) {
              <svg-icon src="assets/icons/Arrows/arrow-narrow-down.svg" />
              {{ 'pages.conflictDetail.saveAndNextButton.label' | translate }}
            } @else {
              <svg-icon src="assets/icons/Arrows/arrow-narrow-left.svg" />
              {{ 'pages.conflictDetail.saveAndBackButton.label' | translate }}
            }
          </app-button>
        }
      </div>
    </div>

    <div class="flex items-center justify-end gap-6">
      @if (addressConflictCounts['NEW_ADDRESS']) {
        <span
          data-test-id="mapLinks"
          class="flex items-center justify-center gap-2 px-8 py-4 text-base"
        >
          <svg-icon src="assets/icons/Maps_travel/map-01.svg" />
          {{ 'pages.conflictDetail.viewOnMap.start' | translate }}
          <a
            class="text-grey-600 active:bg-grey-50"
            target="_blank"
            [href]="
              `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${address?.lat},${address?.lon}`
            "
          >
            {{ 'pages.conflictDetail.viewOnMap.google' | translate }}
          </a>
          /
          <a
            class="text-grey-600 active:bg-grey-50"
            target="_blank"
            [href]="
              `https://www.geoportal-bw.de/?appCtx=abc123&center=${address?.lon},${address?.lat}&scale=5000`
            "
          >
            {{ 'pages.conflictDetail.viewOnMap.geoBW' | translate }}
          </a>
          {{ 'pages.conflictDetail.viewOnMap.end' | translate }}
        </span>
      }

      @if (!showPreviewOnly) {
        <div>
          {{ 'pages.conflictDetail.openConflicts' | translate }}:
          <app-number
            [type]="openAttributeConflicts === 0 ? 'success' : 'error'"
            >{{ openAttributeConflicts ?? '-' }}
          </app-number>
        </div>
        <label class="hover:cursor-pointer" for="showOnlyConflicts">
          {{ 'pages.conflictDetail.showOnlyConflicts' | translate }}
          <nz-switch
            nzId="showOnlyConflicts"
            data-test-id="showOnlyConflictsSwitch"
            [(ngModel)]="showOnlyConflicts"
            (ngModelChange)="onShowOnlyConflicts()"
          />
        </label>
      }
    </div>
  </div>

  <div class="relative flex flex-col gap-6">
    @if (isLoading) {
      <div
        data-test-id="loadingLayer"
        class="absolute inset-0 bg-white opacity-50"
      >
        <div class="flex h-[40vh] items-center justify-center">
          <svg-icon
            src="assets/icons/General/loading-01.svg"
            class="size-14 animate-spin text-blue"
          />
        </div>
      </div>
    }

    @if (unresolvedAttributeConflicts === 0) {
      <app-alert
        data-test-id="alertBarConflictSuccess"
        type="success"
        size="large"
        [showIcon]="true"
      >
        {{ 'pages.conflictDetail.alert.success' | translate }}
      </app-alert>
    } @else {
      @if (showPreviewOnly) {
        @if (addressConflictCounts['NEW_ADDRESS']) {
          <app-alert
            data-test-id="alertBarConflictNew"
            type="info"
            size="large"
            [showIcon]="true"
          >
            <markdown>
              {{ 'pages.conflictDetail.alert.infoNew' | translate }}
            </markdown>
          </app-alert>
        } @else {
          <app-alert
            data-test-id="alertBarConflictInfo"
            type="info"
            size="large"
            [showIcon]="true"
          >
            <markdown>
              {{ 'pages.conflictDetail.alert.info' | translate }}
            </markdown>
          </app-alert>
        }
      }
    }

    @if (masterData.length !== 0) {
      <ng-container
        [ngTemplateOutlet]="tableTemplate"
        [ngTemplateOutletContext]="{
          tableData: masterData,
          label: 'pages.conflictDetail.cluster.masterData',
          typeFilter: 'Stammdaten',
        }"
      />
    }
    @if (dynamicData.length !== 0) {
      <ng-container
        [ngTemplateOutlet]="tableTemplate"
        [ngTemplateOutletContext]="{
          tableData: dynamicData,
          label: 'pages.conflictDetail.cluster.dynamicData',
          typeFilter: 'Bewegungsdaten',
        }"
      />
    }
  </div>
</div>

<ng-template
  #tableTemplate
  let-label="label"
  let-tableData="tableData"
  let-typeFilter="typeFilter"
  [typedTemplate]="tableTemplateTypeToken"
>
  <table [attr.data-test-id]="`table-${typeFilter}`">
    <thead>
      <tr>
        <th class="text-xl text-blue">{{ label | translate }} ...</th>

        @if (allConflictCounts['MOVEMENT_DATA_CONFLICT']) {
          <th data-test-id="duplicateCol" class="text-xl text-blue">
            ... {{ 'pages.conflictDetail.fromMasterData' | translate }}
          </th>
        }
        @if (allConflictCounts['MASTER_DATA_CONFLICT']) {
          <th data-test-id="conflictCol" class="text-xl text-nowrap text-blue">
            ... {{ 'pages.conflictDetail.fromMasterData' | translate }}
            <svg-icon
              class="size-5 cursor-pointer text-grey-400"
              src="assets/icons/General/help-circle.svg"
              nz-tooltip
              [nzTooltipTitle]="
                'pages.conflictDetail.fromMasterDataHelp' | translate
              "
            />
          </th>
        }
        @if (!allConflictCounts['DUPLICATE_IN_CSV']) {
          <th data-test-id="duplicateCol" class="text-xl text-blue">
            ... {{ 'pages.conflictDetail.fromCSV' | translate }}
          </th>
        } @else {
          <th data-test-id="duplicateCol" class="text-xl text-blue">
            ... {{ 'pages.conflictDetail.fromCSV' | translate }} 1
          </th>
          @for (id of conflictTargetAddressIds; track id) {
            <th
              class="text-xl text-blue"
              [attr.data-test-id]="`duplicateCol-${$count + 1}`"
            >
              ... {{ 'pages.conflictDetail.fromCSV' | translate }}
              {{ $count + 1 }}
            </th>
          }
          <th>
            <!-- action -->
          </th>
        }
      </tr>
    </thead>
    <tbody>
      @for (data of tableData; track data.key) {
        <tr class="border-b-1 border-grey-200">
          <td
            class="align-top"
            [ngClass]="{
              'bg-red-50':
                data.target === undefined && data.conflicts.length !== 0,
              'bg-green-50':
                data.target !== undefined && data.conflicts.length !== 0,
            }"
          >
            <ng-container
              [ngTemplateOutlet]="keyCellTemplate"
              [ngTemplateOutletContext]="{
                currentRow: data,
              }"
            />
          </td>

          @for (
            col of data.cols | keyvalue: keyValuePipeOrderCallback;
            track col.key
          ) {
            <td
              class="align-top"
              [ngClass]="{
                'bg-red-50': data.target === undefined && col.value,
                'bg-green-50': data.target !== undefined && col.value,
              }"
            >
              <ng-container
                [ngTemplateOutlet]="conflictCellTemplate"
                [ngTemplateOutletContext]="{
                  currentRow: data,
                  conflict: col.value,
                  key: col.key,
                }"
              />
              @if (showDebug && col.value) {
                <div
                  data-test-id="debugConflict"
                  class="mt-4 rounded-xl bg-grey-200 px-2 py-3 text-xs"
                >
                  <p>{{ col.key | json }}</p>
                  <pre class="whitespace-pre-wrap">{{ col.value | json }}</pre>
                </div>
              }
            </td>
          }
          <td class="align-top">
            <ng-container
              [ngTemplateOutlet]="summaryCellTemplate"
              [ngTemplateOutletContext]="{
                currentRow: data,
              }"
            />
          </td>
        </tr>
      } @empty {
        <tr>
          <td [attr.colspan]="2">
            {{ 'pages.conflictDetail.table.noResult' | translate }}
          </td>
        </tr>
      }
    </tbody>
  </table>
</ng-template>

<ng-template
  #keyCellTemplate
  let-currentRow="currentRow"
  [typedTemplate]="cellTemplateTypeToken"
>
  @let translationKeyQuery = `models.address.${currentRow.key}.label`;
  @let translatedKey = translationKeyQuery | translate;

  @let translationOewNameQuery = `models.address.${currentRow.key}.oew_name`;
  @let translatedOewName = translationOewNameQuery | translate;

  @if (currentRow.attributeType === 'Stammdaten') {
    <strong data-test-id="attributeLabel">{{ translatedKey }}</strong>
  } @else if (currentRow.attributeType === 'Bewegungsdaten') {
    <strong data-test-id="attributeLabel">{{ translatedKey }}</strong>
  } @else {
    @if (translatedOewName === translationOewNameQuery) {
      {{ currentRow.key }}<br />
      ##missing oew_name## ({{ translationOewNameQuery }})<br />
    } @else {
      <strong data-test-id="attributeLabel">{{ translatedOewName }}</strong>
    }
  }
</ng-template>

<ng-template #valueCellTemplate let-value="value">
  <span data-test-id="attributeValue" class="block w-full wrap-break-word">
    @if (value === null) {
      -
    } @else {
      {{ value }}
    }
  </span>
</ng-template>

<ng-template
  #conflictCellTemplate
  let-data="currentRow"
  let-colKey="key"
  let-conflict="conflict"
  [typedTemplate]="cellTemplateTypeToken"
>
  @if (conflict !== undefined) {
    <label
      class="group flex cursor-pointer flex-col items-start gap-2"
      [attr.for]="`${colKey}_${data.key}`"
      [attr.data-test-id]="`conflictInputLabel-${data.key}-${colKey}`"
    >
      <ng-container
        [ngTemplateOutlet]="valueCellTemplate"
        [ngTemplateOutletContext]="{
          value: conflict.value,
        }"
      />

      @if (viewMode === 'clearing') {
        <div class="grid place-items-center">
          <input
            type="radio"
            class="peer col-start-1 row-start-1 size-6 cursor-pointer appearance-none rounded-full border-2 border-grey-300 transition-all group-hover:border-blue-600 checked:border-blue-600 focus:border-grey-300 focus:ring-2 focus:ring-blue-600 focus:ring-offset-1 focus:outline-hidden"
            [attr.data-test-id]="`conflictSelection-${data.key}-${colKey}`"
            [disabled]="isLoading"
            [id]="`${colKey}_${data.key}`"
            [name]="`${colKey}_${data.key}`"
            [value]="conflict.conflictId"
            [tabindex]="colKey === 'CSV' ? -1 : 1"
            [(ngModel)]="data.target"
            (ngModelChange)="onChangeConflict()"
          />
          <div
            class="col-start-1 row-start-1 size-0 rounded-full bg-blue-600 transition-all peer-checked:size-4"
          ></div>
        </div>
      }
    </label>
  } @else {
    <ng-container
      [ngTemplateOutlet]="valueCellTemplate"
      [ngTemplateOutletContext]="{
        value: data.value,
      }"
    />
  }
</ng-template>

<ng-template
  #summaryCellTemplate
  let-currentRow="currentRow"
  [typedTemplate]="cellTemplateTypeToken"
>
  @if (currentRow.target === undefined && currentRow.conflicts.length !== 0) {
    <div data-test-id="conflictIndicatorError" class="flex gap-4 text-red-500">
      <svg-icon src="assets/icons/General/x-circle.svg" />
      <strong>{{
        'pages.conflictDetail.table.conflictUnsolved' | translate
      }}</strong>
    </div>
  } @else if (
    currentRow.target !== undefined && currentRow.conflicts.length !== 0
  ) {
    <div
      data-test-id="conflictIndicatorSuccess"
      class="flex gap-4 text-green-500"
    >
      <svg-icon src="assets/icons/General/check-circle-broken.svg" />
      <strong>{{
        'pages.conflictDetail.table.conflictSolved' | translate
      }}</strong>
    </div>
  }

  @if (showDebug) {
    @if (currentRow.conflicts.length !== 0) {
      <div
        data-test-id="debugConflicts"
        class="mt-4 rounded-sm bg-grey-200 px-2 py-3 text-xs"
      >
        <p class="my-2 text-xs">current:{{ currentRow.value | json }}</p>
        <p class="my-2 text-xs">target:{{ currentRow.target | json }}</p>

        @for (conflict of currentRow.conflicts; track conflict.conflictId) {
          <hr />
          <dl class="mt-2 grid grid-cols-[auto,1fr] place-items-start gap-x-4">
            <dt class="m-0 pb-1">expectedValue:</dt>
            <dd class="m-0 pb-1">{{ conflict.expectedValue | json }}</dd>
            <dt class="m-0 pb-1">targetAddressId:</dt>
            <dd class="m-0 pb-1">{{ conflict.targetAddressId }}</dd>
            <dt class="m-0 pb-1">conflictId:</dt>
            <dd class="m-0 pb-1">{{ conflict.conflictId }}</dd>

            <dt class="m-0 pb-1">validationStatus:</dt>
            <dd class="m-0 pb-1">{{ conflict.validationStatus }}</dd>
            <dt class="m-0 pb-1">isResolution:</dt>
            <dd class="m-0 pb-1">{{ conflict.isResolution | json }}</dd>
          </dl>
        }
      </div>
    }
  }
</ng-template>
